'use strict';

var CallsDevice = require('./util/CallsDevice');
var CmdDevice = require('./util/CmdDevice');
var log = require('../../../log/Log').channel('GroupSeries');

var volumeMin = 0;
var volumeMax = 50;

var stateChangeWaitMs = 1 * 60 * 1000;

var suspendMs = 7000;

function clip(x, min, max) {
    if (x < min) return min;
    if (x > max) return max;
    return x;
}

function GroupSeries(ipAddress, username, password, onConnectStatus, onControlStatus, config) {
    this.publicCalendarEvents = {};
    this.calendarEvents = {};
    this.cameraStates = {};
    this.cameraInfo = {};
    this.cameraCaps = {};
    this.previousCameraTrackingValue = {};
    this.dialDigits = '';
    this.dialTimeout = null;
    this.pollCycle = 0;
    this.longPollCycle = 0;
    this.contentActive = false;

    this.httpRequestBaseUrl = 'https://' + ipAddress;

    CallsDevice.call(this);
    CmdDevice.call(this, ipAddress, username, password, onConnectStatus, onControlStatus, config);

    this.onCallStateChanged(this.callStateChangedHandler);
}

GroupSeries.prototype = Object.create(CmdDevice.prototype);
GroupSeries.prototype.constructor = GroupSeries;

Object.assign(GroupSeries.prototype, CallsDevice.prototype);

GroupSeries.prototype.name = 'GroupSeries';
GroupSeries.prototype.key = '-L9MgtNs7sBD1JDMpBlr';
GroupSeries.prototype.httpRequestEnable = true;
GroupSeries.prototype.httpRequestBaseUrl = null;
GroupSeries.prototype.httpRequestDataFormat = 'json';
GroupSeries.prototype.pingAttemptLimit = 3;
GroupSeries.prototype.pingMs = 3000;
GroupSeries.prototype.pollIntervalMs = 3000;

GroupSeries.prototype.cameraLimits = {
    x: { min: -50000, max: 50000 },
    y: { min: -50000, max: 50000 },
    z: { min: -50000, max: 50000 },
};
GroupSeries.prototype.volumeLimits = {
    min: 0,
    max: 100,
};

GroupSeries.prototype.simulationParams = {
    caps: {
        hasCalendar: true,
        hasCameraTracking: true,
        hasFirmwareVersion: true,
        hasRoomMute: true,
        hasRoomVolume: true,
        hasRoomVolumeMute: true,
        hasVideoInputs: true,
        hasVideoSwitcher: true,
        maxCalls: {
            pstn: 0,
            video: 1,
            voip: 0,
        },
        numCameras: 1,
        videoInputsAreContent: true,
    },
    callHasReceiveGain: function (ci, config, detected) {
        return false;
    },
    configNotRequired: {
        roomMute: true,
        roomVolume: true,
    },
    // detected: not supported on GroupSeries
    firmwareVersion: '6.1.5',
    // channelsAreEqual: not supported on GroupSeries
    // channelMatchesDetected: not supported on GroupSeries
    // getTelephonyType: not supported on GroupSeries
    // telephonyChannels: not supported on GroupSeries
};

GroupSeries.prototype.commands = {};

GroupSeries.prototype.commands.$deleteCurrentNotifications = {};
GroupSeries.prototype.commands.$deleteCurrentNotifications.activeStates = ['initializing'];
GroupSeries.prototype.commands.$deleteCurrentNotifications.activeInitializeStates = [
    'registeringNotifications',
];
GroupSeries.prototype.commands.$deleteCurrentNotifications.get = function () {
    this.sendRequest('$deleteCurrentNotifications', 'DELETE', '/rest/current/notifications');
};
GroupSeries.prototype.commands.$deleteCurrentNotifications.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.$meeting = {};
GroupSeries.prototype.commands.$meeting.match = function (resp) {
    if (resp.hasOwnProperty('id')) return resp;
    return null;
};
GroupSeries.prototype.commands.$meeting.process = function (meeting) {
    if (!this.tmpCalendarEvents.hasOwnProperty(meeting.id)) {
        log.error(this + ': unexpected meeting id: ' + JSON.stringify(meeting, null, 4));
        return;
    }

    var event = this.tmpCalendarEvents[meeting.id];

    if (meeting.hasOwnProperty('body')) {
        event.description = {
            contentType: 'html',
            content: this.parseMeetingBody(meeting.body),
        };
    }

    if (event.last) {
        delete event.last;

        // GroupSeries sends calendar onMeetingChanged notifications every
        // minute, even when the meeting information hasn't changed. We make
        // sure that the meeting information actually has changed before
        // broadcasting new values (which triggers a database write).

        var changed = !this.objEquals(this.tmpCalendarEvents, this.calendarEvents);

        this.calendarEvents = this.tmpCalendarEvents;

        if (changed) {
            this.publicCalendarEvents = {};

            // Make a copy of the calendar events using firebase-style keys.

            for (var id in this.calendarEvents) {
                if (!this.calendarEvents.hasOwnProperty(id)) continue;
                this.publicCalendarEvents[this.generateId()] = JSON.parse(
                    JSON.stringify(this.calendarEvents[id])
                );
            }
        }

        if (changed && this.config.useCalendar) {
            this.broadcastStatus('calendar', { events: this.publicCalendarEvents });
        }
    }
};

GroupSeries.prototype.commands.$meetings = {};
GroupSeries.prototype.commands.$meetings.get = function () {
    if (this.hasRequest('$meetings') || this.hasRequest('$meeting')) return;
    this.sendRequest('$meetings', 'GET', '/rest/calendar/meetings?number=120');
};
GroupSeries.prototype.commands.$meetings.match = function (resp) {
    if (Array.isArray(resp)) return resp;
    return null;
};
GroupSeries.prototype.commands.$meetings.process = function (resp) {
    var actionEmailRegex = /^sip:([^@;]+@[^@;]+);/;

    this.tmpCalendarEvents = {};

    if (resp.length <= 0) {
        if (this.fsm.current === 'initializing') {
            this.doNextInitializeStep();
        } else if (this.config.useCalendar) {
            this.broadcastStatus('calendar', { events: this.tmpCalendarEvents });
        }

        return;
    }

    for (var i = 0; i < resp.length; i++) {
        var meeting = resp[i];
        var event = {};
        var j;
        var n;

        // Use 'a0', 'a1', ... style keys in the object so we can later compare
        // using objEquals to see if any meeting information changed.

        if (meeting.hasOwnProperty('subject')) {
            if (meeting.subject) {
                event.summary = meeting.subject;
            } else {
                event.summary = '';
            }
        }

        if (meeting.hasOwnProperty('startTime')) {
            if (!meeting.startTime) {
                this.log.error('no startTime for meeting');
                event.start = Date.now();
            } else {
                event.start = meeting.startTime * 1000;
            }

            if (meeting.duration) {
                event.end = event.start + meeting.duration * 1000;
            }
        }

        if (meeting.organizer) {
            event.organizer = { name: meeting.organizer };
        }

        n = 0;

        if (meeting.requiredAttendees) {
            event.attendees = {};

            for (j = 0; j < meeting.requiredAttendees.length; j++) {
                event.attendees['a' + n] = {
                    name: meeting.requiredAttendees[j],
                    type: 'required',
                };
                n++;
            }
        }

        if (meeting.optionalAttendees) {
            if (!event.hasOwnProperty('attendees')) event.attendees = {};

            for (j = 0; j < meeting.optionalAttendees.length; j++) {
                event.attendees['a' + n] = {
                    name: meeting.optionalAttendees[j],
                    type: 'optional',
                };
                n++;
            }
        }

        if (meeting.location) {
            event.location = { name: meeting.location };
        }

        if (meeting.actions) {
            var callMethods = {};

            n = 0;

            for (j = 0; j < meeting.actions.length; j++) {
                callMethods['a' + n] = {
                    callType: 'video',
                    dialString: meeting.actions[j],
                };

                var match = actionEmailRegex.exec(meeting.actions[j]);

                if (match !== null) {
                    callMethods['a' + n].displayDialString = match[1];
                }

                n++;
            }

            if (Object.keys(callMethods).length > 0) {
                event.callMethods = callMethods;
            }
        }

        if (i === resp.length - 1) event.last = true;

        this.tmpCalendarEvents[meeting.id] = event;

        this.sendRequest('$meeting', 'GET', '/rest/calendar/meetings/' + meeting.id);
    }
};

GroupSeries.prototype.commands.$calls = {};
GroupSeries.prototype.commands.$calls.activeStates = [
    'initializing',
    'controlling',
    'controlFailed',
];
GroupSeries.prototype.commands.$calls.activeInitializeStates = ['gettingCalls'];
GroupSeries.prototype.commands.$calls.get = function () {
    var path = this.newApi ? '/rest/current/conferences' : '/rest/conferences';
    this.sendRequest('$calls', 'GET', path);
};
GroupSeries.prototype.commands.$calls.match = function (resp) {
    if (Array.isArray(resp)) return resp;
    return null;
};
GroupSeries.prototype.commands.$calls.process = function (resp) {
    if (this.fsm.current === 'initializing') {
        this.initializeCallInterfaces(this.parseConferences(resp));
        this.doNextInitializeStep();
    } else {
        this.updateCallInterfaces(this.parseConferences(resp));
    }
};

GroupSeries.prototype.commands.$cameras = {};
GroupSeries.prototype.commands.$cameras.activeStates = ['initializing', 'controlling'];
GroupSeries.prototype.commands.$cameras.activeInitializeStates = ['gettingCameras'];
GroupSeries.prototype.commands.$cameras.get = function () {
    var path = this.newApi ? '/rest/current/cameras/near/all' : '/rest/cameras/near/all';
    if (this.hasRequest('$cameras')) return;
    this.query('$power'); // make sure we have the most recent power status
    this.sendRequest('$cameras', 'GET', path);
};
GroupSeries.prototype.commands.$cameras.match = function (resp) {
    if (Array.isArray(resp)) return resp;
    return null;
};
GroupSeries.prototype.commands.$cameras.process = function (resp) {
    // When the Group Series is asleep, it powers down the cameras, and they
    // report false for all their caps. We don't update our camera info when
    // the Group Series is asleep since we don't want to report the powered
    // down caps.

    if (!this.power) {
        if (this.fsm.current === 'initializing') {
            this.doNextInitializeStep();
        }

        return;
    }

    // See if any cameras have been added/removed or if their capabilities
    // have changed, then broadcast new caps if they have.

    var newCameraCaps = {};
    var newCameraStates = {};
    var newCameraInfo = {};
    var cameraId;
    var entry;
    var i;

    for (i = 0; i < resp.length; i++) {
        entry = resp[i];

        if (entry.sourceType === 'SRC_PEOPLE' && entry.nearCamera && entry.connected) {
            var foundCameraId = null;

            for (cameraId in this.cameraInfo) {
                if (!this.cameraInfo.hasOwnProperty(cameraId)) continue;
                if (this.cameraInfo[cameraId].index === entry.cameraIndex) {
                    foundCameraId = cameraId;
                    break;
                }
            }

            cameraId = foundCameraId === null ? this.generateId() : foundCameraId;

            newCameraCaps[cameraId] = {
                name: entry.name,
                hasCameraTracking: entry.trackable,
                hasCameraTrackingUi:
                    entry.trackable && this.getNode(this.config, 'cameraTrackingUiEnabled', true),
                hasPan: entry.hasPan,
                hasTilt: entry.hasTilt,
                hasZoom: entry.hasZoom,
                location: entry.nearCamera ? 'near' : 'far',
            };

            newCameraStates[cameraId] = {
                selected: entry.selected,
                // use previous value for tracking, it will be handled with $cameraTracking
                tracking:
                    this.cameraStates.hasOwnProperty(cameraId) &&
                    this.cameraStates[cameraId].hasOwnProperty('tracking')
                        ? this.cameraStates[cameraId].tracking
                        : false,
            };

            newCameraInfo[cameraId] = {
                index: entry.cameraIndex,
            };
        }
    }

    if (!this.objEquals(this.cameraCaps, newCameraCaps)) {
        this.cameraCaps = newCameraCaps;
        this.cameraInfo = newCameraInfo;

        if (this.fsm.current === 'controlling') {
            this.computeCaps();
        }
    }

    if (!this.objEquals(this.cameraStates, newCameraStates)) {
        this.cameraStates = newCameraStates;

        if (this.fsm.current === 'controlling') {
            this.query('cameraSelected');
        }
    }

    // Look for content source changes, and update state as necessary.

    var newContentSource = null;
    var otherContentSourceSelected = false;

    for (i = 0; i < resp.length; i++) {
        entry = resp[i];

        // If a content source is selected, see if it matches one of the codes
        // in our configuration.

        // G7500 isn't showing content as selected when a content source is enabled
        // so we use connected and the new poll $contentActive that shows content is active.

        if (
            entry.sourceType === 'SRC_CONTENT' &&
            (entry.selected || (entry.connected && this.contentActive === true))
        ) {
            var srcIds = Object.keys(this.config.videoInputs);
            var foundContentSource = false;

            for (var j = 0; j < srcIds.length; j++) {
                if (entry.cameraIndex.toString() === this.config.videoInputs[srcIds[j]].code) {
                    newContentSource = srcIds[j];
                    break;
                }
            }

            if (!foundContentSource) otherContentSourceSelected = true;
        }

        if (newContentSource !== null) break;
    }

    if (newContentSource === null) {
        if (!otherContentSourceSelected) {
            newContentSource = this.noContentId;
        }
    }

    if (newContentSource !== this.contentSource) {
        if (this.contentSource !== this.noContentId && this.fsm.current === 'controlling') {
            this.broadcastStatus('contentState', {
                id: this.contentSource,
                state: null,
            });
        }

        this.contentSource = newContentSource;

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('contentSource', this.contentSource);

            if (this.contentSource !== this.noContentId) {
                this.broadcastStatus('contentState', {
                    id: this.contentSource,
                    state: 'sending',
                });
            }
        }
    }

    if (this.fsm.current === 'initializing') {
        this.doNextInitializeStep();
    }
};

GroupSeries.prototype.commands.$cameraTracking = {};
GroupSeries.prototype.commands.$cameraTracking.activeStates = ['initializing', 'controlling'];
GroupSeries.prototype.commands.$cameraTracking.activeInitializeStates = ['gettingCameraTracking'];
GroupSeries.prototype.commands.$cameraTracking.get = function () {
    if (this.hasRequest('$cameraTracking')) return;

    var cameraNames = [];

    for (var cameraId in this.cameraCaps) {
        if (!this.cameraCaps.hasOwnProperty(cameraId)) continue;
        if (this.cameraCaps[cameraId].hasCameraTracking) {
            cameraNames.push(
                'sourceman.camera' + this.cameraInfo[cameraId].index + '.trackingmode'
            );
        } else {
            this.cameraStates[cameraId].tracking = false;
        }
    }

    if (cameraNames.length === 0) {
        if (this.fsm.current === 'initializing') this.doNextInitializeStep();
    } else {
        var path = this.newApi ? '/rest/current/config' : '/rest/config';
        this.sendRequest('$cameraTracking', 'POST', path, { names: cameraNames });
    }
};
GroupSeries.prototype.commands.$cameraTracking.match = function (resp) {
    if (resp.hasOwnProperty('vars') && Array.isArray(resp.vars)) return resp;
    return null;
};
GroupSeries.prototype.commands.$cameraTracking.process = function (resp) {
    var cameraIdRegex = /sourceman\.camera(\d+)\.trackingmode/;
    var trackingChanged = false;

    for (var i = 0; i < resp.vars.length; i++) {
        var entry = resp.vars[i];
        if (!entry.hasOwnProperty('name') || !entry.hasOwnProperty('value')) continue;

        var match = cameraIdRegex.exec(entry.name);

        if (match) {
            var cameraIndex = parseInt(match[1], 10);
            var foundCameraId = null;

            for (var cameraId in this.cameraInfo) {
                if (!this.cameraInfo.hasOwnProperty(cameraId)) continue;
                if (this.cameraInfo[cameraId].index === cameraIndex) {
                    foundCameraId = cameraId;
                    break;
                }
            }

            if (foundCameraId === null) {
                log.error(this + ': unexpected camera index: ' + entry.name);
            } else {
                var tracking = entry.value !== 'Off';

                if (
                    !this.cameraStates[cameraId].hasOwnProperty('tracking') ||
                    this.cameraStates[cameraId].tracking !== tracking
                ) {
                    this.cameraStates[cameraId].tracking = tracking;
                    trackingChanged = true;
                }

                if (tracking) {
                    this.previousCameraTrackingValue[cameraId] = entry.value;
                }
            }
        } else {
            log.error(this + ': unexpected config name: ' + entry.name);
        }
    }

    if (this.fsm.current === 'initializing') {
        this.doNextInitializeStep();
    } else if (trackingChanged) {
        this.query('cameraTracking');
    }
};

GroupSeries.prototype.commands.$contentActive = {};
GroupSeries.prototype.commands.$contentActive.activeStates = ['initializing', 'controlling'];
GroupSeries.prototype.commands.$contentActive.activeInitializeStates = ['gettingCameraTracking'];
GroupSeries.prototype.commands.$contentActive.get = function () {
    if (this.hasRequest('$contentActive')) return;
    var path = this.newApi ? '/rest/cameras/contentstatus' : '/rest/cameras/contentstatus';
    this.sendRequest('$contentActive', 'GET', path);
};
GroupSeries.prototype.commands.$contentActive.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.$contentActive.process = function (resp) {
    var contentActiveRegExp = /CONTENT_(.+)/i;
    var amatch = contentActiveRegExp.exec(resp);

    if (amatch !== null) {
        if (amatch[1] !== 'IDLE') {
            this.contentActive = true;
        } else {
            this.contentActive = false;
        }
    }
    log.debug('active content value = ' + this.contentActive);
};

GroupSeries.prototype.commands.$logIn = {};
GroupSeries.prototype.commands.$logIn.activeStates = ['connecting'];
GroupSeries.prototype.commands.$logIn.receiveFullResponse = true;
GroupSeries.prototype.commands.$logIn.get = function () {
    var username =
        typeof this.username === 'string' && this.username !== '' ? this.username : 'admin';
    var password = typeof this.password === 'string' ? this.password : '';

    this.sendRequest('$logIn', 'POST', '/rest/session', {
        action: 'Login',
        user: username,
        password: password,
    });
};
GroupSeries.prototype.commands.$logIn.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.$logIn.process = function (resp) {
    if (resp.status === 200) {
        var data = {};
        try {
            data = JSON.parse(resp.data);
        } catch (e) {} // eslint-disable-line no-empty

        if (
            data.success &&
            data.hasOwnProperty('session') &&
            data.session.hasOwnProperty('sessionId')
        ) {
            log.debug(this + ': authentication successful');
            this.finishConnecting();
            return;
        }
    }

    if (resp.status === 403) {
        var error = {};
        try {
            error = JSON.parse(resp.error);
        } catch (e) {} // eslint-disable-line no-empty

        if (error.hasOwnProperty('reason') && error.reason === 'SessionInvalidUserNamePassword') {
            log.debug(this + ': invalid username or password');
            this.fsm.failAuth();
            return;
        }
    }

    log.debug(this + ': authentication failed, response: ' + JSON.stringify(resp, null, 4));

    if (resp.status === -1) {
        this.fsm.failConnect();
    } else {
        this.fsm.failVerify();
    }
};

GroupSeries.prototype.commands.$logInCurrent = {};
GroupSeries.prototype.commands.$logInCurrent.activeStates = ['connecting'];
GroupSeries.prototype.commands.$logInCurrent.receiveFullResponse = true;
GroupSeries.prototype.commands.$logInCurrent.get = function () {
    var username =
        typeof this.username === 'string' && this.username !== '' ? this.username : 'admin';
    var password = typeof this.password === 'string' ? this.password : '';

    this.sendRequest('$logInCurrent', 'POST', '/rest/current/session', {
        user: username,
        password: password,
    });
};
GroupSeries.prototype.commands.$logInCurrent.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.$logInCurrent.process = function (resp) {
    if (resp.status === 200) {
        var data = {};
        try {
            data = JSON.parse(resp.data);
        } catch (e) {} // eslint-disable-line no-empty

        if (
            data.success &&
            data.hasOwnProperty('session') &&
            data.session.hasOwnProperty('sessionId')
        ) {
            log.debug(this + ': authentication successful');
            this.finishConnecting();
            return;
        }
    }

    if (resp.status === 403) {
        var error = {};
        try {
            error = JSON.parse(resp.error);
        } catch (e) {} // eslint-disable-line no-empty

        if (error.hasOwnProperty('reason') && error.reason === 'SessionInvalidUserNamePassword') {
            log.debug(this + ': invalid username or password');
            this.fsm.failAuth();
            return;
        }
    }

    // If we failed to login with the new API, and there wasn't an authentication issue, then try
    // logging in with the old API.

    if (resp.status >= 400) {
        log.debug(this + `: Using the old API.`);
        this.newApi = false;
        this.query('$logOut');
        return;
    }

    log.debug(this + ': authentication failed, response: ' + JSON.stringify(resp, null, 4));

    if (resp.status === -1) {
        this.fsm.failConnect();
    } else {
        this.fsm.failVerify();
    }
};

GroupSeries.prototype.commands.$logOut = {};
GroupSeries.prototype.commands.$logOut.activeStates = ['connecting'];
GroupSeries.prototype.commands.$logOut.receiveFullResponse = true;
GroupSeries.prototype.commands.$logOut.get = function () {
    this.sendRequest('$logOut', 'POST', '/rest/session', { action: 'Logout' });
};
GroupSeries.prototype.commands.$logOut.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.$logOut.process = function (resp) {
    this.query('$logIn');
};

GroupSeries.prototype.commands.$logOutCurrent = {};
GroupSeries.prototype.commands.$logOutCurrent.activeStates = ['connecting'];
GroupSeries.prototype.commands.$logOutCurrent.receiveFullResponse = true;
GroupSeries.prototype.commands.$logOutCurrent.get = function () {
    this.sendRequest('$logOutCurrent', 'DELETE', '/rest/current/session');
};
GroupSeries.prototype.commands.$logOutCurrent.match = function (resp) {
    this.query('$logInCurrent');
};

GroupSeries.prototype.commands.$initialMute = {};
GroupSeries.prototype.commands.$initialMute.activeStates = ['initializing'];
GroupSeries.prototype.commands.$initialMute.activeInitializeStates = ['gettingMute'];
GroupSeries.prototype.commands.$initialMute.get = function () {
    var path = this.newApi ? '/rest/current/audio/muted' : '/rest/audio/muted';
    this.sendRequest('$initialMute', 'GET', path);
};
GroupSeries.prototype.commands.$initialMute.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.$initialMute.process = function (resp) {
    this.updateMute(resp);
    this.doNextInitializeStep();
};

GroupSeries.prototype.commands.$initialVolume = {};
GroupSeries.prototype.commands.$initialVolume.activeStates = ['initializing'];
GroupSeries.prototype.commands.$initialVolume.activeInitializeStates = ['gettingVolume'];
GroupSeries.prototype.commands.$initialVolume.get = function () {
    // The 4.0.0 REST API guide for the G7500 indicates /rest/audio/volume
    // but in testing with 4.1.0, that command indicates it is a bad request.
    // Requesting /rest/audio returns an object that has 'volume' as a key.

    var path = this.newApi ? '/rest/current/audio' : '/rest/audio/volume';
    this.sendRequest('$initialVolume', 'GET', path);
};
GroupSeries.prototype.commands.$initialVolume.match = function (resp) {
    return this.newApi ? resp.volume : resp;
};
GroupSeries.prototype.commands.$initialVolume.process = function (resp) {
    this.updateVolume(resp);
    this.doNextInitializeStep();
};

GroupSeries.prototype.commands.$notifications = {};
GroupSeries.prototype.commands.$notifications.activeStates = ['initializing', 'controlling'];
GroupSeries.prototype.commands.$notifications.isAsync = true;
GroupSeries.prototype.commands.$notifications.receiveFullResponse = true;
GroupSeries.prototype.commands.$notifications.get = function () {
    this.sendRequest(
        '$notifications',
        'GET',
        this.notificationsUrl + '&limit=100&wait=30',
        null,
        null,
        33000
    );
};
GroupSeries.prototype.commands.$notifications.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.$notifications.process = function (resp) {
    if (resp.status === 204) {
        // This happens when there are no notifications for the specified wait
        // time. A new query will be started at the end of this function.
    } else if (resp.status === 500) {
        // We have seen this "internal server error" from systems in the field.
        // If we get this error, wait a couple of seconds, then try again.

        this.defer(
            function () {
                this.query('$notifications');
            }.bind(this),
            2000
        );
    } else if (resp.status === 200) {
        if (resp.hasOwnProperty('data')) {
            var data = JSON.parse(resp.data);

            if (data.hasOwnProperty('entries')) {
                var newMute = null;
                var newVolume = null;
                var newSipEnabled = null;
                var newLastPresetIndex = null;
                var gotCameraNotifications = false;
                var gotCalendarNotifications = false;

                for (var i = 0; i < data.entries.length; i++) {
                    var entry = data.entries[i];
                    if (entry.name === 'onNearMuteChange') {
                        newMute = entry.object;
                    } else if (entry.name === 'onVolumeChange') {
                        newVolume = entry.object;
                    } else if (entry.eventPackage === 'conference') {
                        this.handleConferenceEvent(entry);
                    } else if (
                        this.getNode(entry, ['object', 'name']) ===
                        'comm.callpreference.networkdialing.availabledialtypes'
                    ) {
                        // We only get this notification with the old API. For the new API
                        // we poll the sipEnabled status.
                        newSipEnabled = this.getNode(entry, ['object', 'value'], []).includes(
                            'sip'
                        );
                    } else if (entry.eventPackage === 'camera') {
                        if (entry.name === 'onPresetActivated') {
                            newLastPresetIndex = entry.object.index.toString();
                        }
                        gotCameraNotifications = true;
                    } else if (entry.eventPackage === 'calendar') {
                        gotCalendarNotifications = true;
                    }
                }

                if (newMute !== null) this.updateMute(newMute);
                if (newVolume !== null) this.updateVolume(newVolume);
                if (newSipEnabled !== null) this.updateSipEnabled(newSipEnabled);

                if (this.config.hasOwnProperty('cameraPresets') && newLastPresetIndex !== null) {
                    var newLastPreset = null;

                    for (var presetId in this.config.cameraPresets) {
                        if (!this.config.cameraPresets.hasOwnProperty(presetId)) continue;

                        if (
                            newLastPresetIndex === this.config.cameraPresets[presetId].presetIndex
                        ) {
                            newLastPreset = presetId;
                            break;
                        }
                    }

                    if (newLastPreset !== this.lastCameraPreset) {
                        this.lastCameraPreset = newLastPreset;
                        this.broadcastStatus('cameraPreset', this.lastCameraPreset);
                    }
                }

                if (gotCameraNotifications) {
                    // Wait a little bit before handling the cameras data. This
                    // is for the case where the Group Series is going to sleep
                    // and powers down the cameras. When that happens, the
                    // cameras report false for all their caps, but we don't
                    // want to reflect that. There is code in the $cameras
                    // process function to handle that, but it relies on the
                    // power state being reported first. So, we delay the
                    // $cameras query to  make sure that's the case.

                    this.defer(
                        function () {
                            this.query('$cameras');
                            this.query('$cameraTracking');
                        }.bind(this),
                        1500
                    );
                }

                if (gotCalendarNotifications) {
                    // Defer querying of meetings since usually we get several
                    // notifications at once. This helps us only query and
                    // broadcast once for a series of notifications.

                    this.defer(
                        function () {
                            this.query('$meetings');
                        }.bind(this),
                        1500
                    );
                }
            } else {
                log.error(
                    this +
                        ': missing entries property in notifications response: ' +
                        JSON.stringify(resp, null, 4)
                );
            }

            if (data.hasOwnProperty('next') && data.next.hasOwnProperty('href')) {
                this.notificationsUrl = data.next.href + '&limit=100&wait=30';
            }
        } else {
            log.error(
                this +
                    ': missing next href in notifications response: ' +
                    JSON.stringify(resp, null, 4)
            );
        }
    } else if (resp.status === -1) {
        log.warning(
            this +
                ': error in notifications response, status = ' +
                resp.status +
                ', resp = ' +
                JSON.stringify(resp)
        );
    } else {
        log.error(
            this +
                ': unexpected notifications response, status = ' +
                resp.status +
                ', resp = ' +
                JSON.stringify(resp)
        );
    }

    this.query('$notifications');
};

GroupSeries.prototype.commands.$power = {};
GroupSeries.prototype.commands.$power.activeStates = ['initializing', 'controlling'];
GroupSeries.prototype.commands.$power.get = function () {
    var path = this.newApi ? '/rest/current/system' : '/rest/system';
    this.sendRequest('$power', 'GET', path);
};
GroupSeries.prototype.commands.$power.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.$power.process = function (resp) {
    if (resp.hasOwnProperty('state')) {
        this.updatePower(resp.state !== 'ASLEEP');
    }
};

GroupSeries.prototype.commands.$registerNotifications = {};
GroupSeries.prototype.commands.$registerNotifications.activeStates = ['initializing'];
GroupSeries.prototype.commands.$registerNotifications.activeInitializeStates = [
    'registeringNotifications',
];
GroupSeries.prototype.commands.$registerNotifications.receiveFullResponse = true;
GroupSeries.prototype.commands.$registerNotifications.get = function () {
    var path = this.newApi ? '/rest/current/notifications' : '/rest/notifications';
    var eventPackage = ['audio', 'conference', 'calendar', 'camera', 'video', 'config'];
    var configName = [
        'video.dualdisplay',
        'video.monitor.monitordisplay',
        'sourceman.camera.trackingmode',
        'comm.callpreference.networkdialing.availabledialtypes',
    ];

    if (!this.newApi) eventPackage.push('roomcontrol');

    var data = {
        eventPackage: eventPackage.join(','),
        configName: configName.join(','),
    };

    if (this.newApi) data.provisioningName = '';

    if (this.newApi) {
        this.sendRequest('$deleteCurrentNotifications', 'DELETE', '/rest/current/notifications');
    }

    this.sendRequest('$registerNotifications', 'POST', path, data);
};
GroupSeries.prototype.commands.$registerNotifications.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.$registerNotifications.process = function (resp) {
    if (resp.status === 201) {
        var locationRegex = this.newApi
            ? /\/rest\/current\/notifications\/(.+)/
            : /\/rest\/notifications\/(.+)/;
        var match = locationRegex.exec(resp.headers.location);
        var path = this.newApi ? '/rest/current/notifications/' : '/rest/notifications/';

        if (match === null) {
            log.error(
                this +
                    ': unexpected response to notifications registration' +
                    JSON.stringify(resp, null, 4)
            );
            return;
        }

        this.notificationsUrl = path + match[1] + '?start=1';
        log.debug(this + ': notifications registered: subscriptionId = ' + match[1]);
        this.query('$notifications');
    } else if (resp.status === 500) {
        log.warning(
            this +
                ': internal server error in response to registering notifications: ' +
                JSON.stringify(resp, null, 4)
        );
        log.debug(this + ': trying again to register notifications');
        this.query('$registerNotifications');
        return;
    } else {
        log.error(
            this +
                ': unexpected response to registering notifications: ' +
                JSON.stringify(resp, null, 4)
        );
    }

    this.doNextInitializeStep();
};

GroupSeries.prototype.commands.$sipEnabled = {};
GroupSeries.prototype.commands.$sipEnabled.activeStates = ['initializing', 'controlling'];
GroupSeries.prototype.commands.$sipEnabled.activeInitializeStates = ['gettingSipEnabled'];
GroupSeries.prototype.commands.$sipEnabled.get = function () {
    var path = this.newApi ? '/rest/current/config/options' : '/rest/config';
    this.sendRequest('$sipEnabled', 'POST', path, {
        names: ['comm.callpreference.networkdialing.availabledialtypes'],
    });
};
GroupSeries.prototype.commands.$sipEnabled.match = function (resp) {
    return resp.hasOwnProperty('vars') ? resp : null;
};
GroupSeries.prototype.commands.$sipEnabled.process = function (resp) {
    var newSipEnabled = false;
    var sipEnabled = this.getConfigVar(
        resp,
        'comm.callpreference.networkdialing.availabledialtypes'
    );

    if (sipEnabled === null) {
        log.error(
            this + ': invalid response to sipenable query: ' + JSON.stringify(resp.vars, null, 4)
        );
        return;
    } else {
        newSipEnabled = sipEnabled.options.includes('sip');
    }

    if (this.fsm.current === 'initializing') {
        this.cleanUpCallInterfaces(); // This will generate the initial call interfaces.
        this.doNextInitializeStep();
    } else {
        // this.fsm.current === 'controlling'
        this.updateSipEnabled(newSipEnabled);
    }
};

GroupSeries.prototype.commands.$version = {};
GroupSeries.prototype.commands.$version.activeStates = ['initializing'];
GroupSeries.prototype.commands.$version.activeInitializeStates = ['gettingVersion'];
GroupSeries.prototype.commands.$version.get = function () {
    var path = this.newApi ? '/rest/current/config' : '/rest/config';
    this.sendRequest('$version', 'POST', path, {
        names: ['system.info.humanreadablemodel', 'system.info.humanreadableversion'],
    });
};
GroupSeries.prototype.commands.$version.match = function (resp) {
    return resp.hasOwnProperty('vars') ? resp : null;
};
GroupSeries.prototype.commands.$version.process = function (resp) {
    var model = this.getConfigVar(resp, 'system.info.humanreadablemodel');
    var version = this.getConfigVar(resp, 'system.info.humanreadableversion');

    if (model !== null) this.model = model.value;
    if (version !== null) this.firmwareVersion = version.value;

    if (model === null || version === null) {
        log.error(this + ': unexpected config response: ' + JSON.stringify(resp, null, 4));
    }

    this.doNextInitializeStep();
};

GroupSeries.prototype.commands.answer = {};
GroupSeries.prototype.commands.answer.receiveFullResponse = true;
GroupSeries.prototype.commands.answer.set = function (callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error(this + ': answer: invalid callId ' + callId);
        return;
    }

    this.answerCall(ci);

    var action =
        this.getNode(ci, ['callStatus', 'callExtendedState']) === 'answerWillEnd'
            ? 'hangupAndAnswer'
            : 'answer';

    var path = this.newApi ? '/rest/current/conferences/' : '/rest/conferences/';
    this.suspend(suspendMs);
    this.sendRequest('answer', 'POST', path + ci.conferenceId + '/connections/' + ci.connectionId, {
        action: action,
    });
    this.queryCallsDeferred();
};
GroupSeries.prototype.commands.answer.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.answer.process = function (resp) {
    if (resp.status === 200 && resp.data === '-1') {
        var match = /\/connections\/(\d+)$/.exec(resp.url);

        if (match === null) {
            log.error(this + ': answer: missing connection id in response url');
            return;
        }

        var ci = this.getCallInterfaceByConnection(match[1]);

        if (ci === null) {
            log.error(this + ': answer: no call interface with connectionId ' + match[1]);
            return;
        }

        if (this.getCallState(ci) === 'connected') {
            // This is a bit of a hack, but setIncomingCall would generate an
            // error because the call is in the "connected" state.

            ci.callStatus.callState = 'ringing';
            ci.callStatus.canDial = false;

            if (this.fsm.current === 'controlling') {
                this.broadcastStatus('callStatus', JSON.parse(JSON.stringify(ci.callStatus)));
            }
        }
    }
};

GroupSeries.prototype.commands.busy = {};
GroupSeries.prototype.commands.busy.get = function () {
    this.broadcastStatus('busy', this.busy);
};

GroupSeries.prototype.commands.calendar = {};
GroupSeries.prototype.commands.calendar.get = function () {
    this.broadcastStatus('calendar', { events: this.publicCalendarEvents });
};

GroupSeries.prototype.commands.call = {};
GroupSeries.prototype.commands.call.set = function (value) {
    var ci = this.getAvailableCallInterface(value.callType);

    if (ci === null) {
        log.error('call: no ' + value.callType + ' resources available');
        return;
    }

    // We need to call placeCall, but we don't known the connectionId yet. So,
    // we setSaveCallId and delete the connectionId on the call interface.
    // This lets updateConnection know to set the connectionId when we get
    // notifications for the call.

    if (typeof this.saveCallId !== 'undefined') {
        log.error(
            this +
                ': setting saveCallId when already set; current = ' +
                this.saveCallId +
                ', new = ' +
                value.callId
        );
    }

    this.saveCallId = value.callId; // save for when we finally call connectCall
    this.placeCall(ci, value.callId, value.dialString);
    delete ci.connectionId;

    this.suspend(suspendMs);
    var path = this.newApi ? '/rest/current/conferences' : '/rest/conferences';
    this.sendRequest('call', 'POST', path, {
        address: value.dialString,
        dialType: value.callType === 'video' ? 'AUTO' : 'VOICE',
        rate: 0,
    });
    this.queryCallsDeferred();
};
GroupSeries.prototype.commands.call.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.callStatus = {};
GroupSeries.prototype.commands.callStatus.get = function () {
    this.sendCallStatusMessages();
};

GroupSeries.prototype.commands.cameraMove = {};
GroupSeries.prototype.commands.cameraMove.set = function (value) {
    if (!value.hasOwnProperty('action')) return;

    var index = null;
    var path = this.newApi ? '/rest/cameras/near/' : '/rest/cameras/near/';

    if (!this.cameraInfo.hasOwnProperty(value.cameraId)) {
        log.error(this + ': cameraMove.set: invalid cameraId ' + value.cameraId);
        return;
    }

    index = this.cameraInfo[value.cameraId].index;

    // The recent API document indicates the controls are MOVE_LEFT, MOVE_RIGHT, MOVE_UP, MOVE_DOWN,
    // MOVE_ZOOMIN, MOVE_ZOOMOUT but these commands don't work for direction.  The traditional left,
    // right, up, down, zoom in, zoom out do.

    if (value.action === 'start') {
        var direction = value.direction;

        if (direction === 'in') {
            direction = 'zoom in';
        } else if (direction === 'out') {
            direction = 'zoom out';
        }

        if (this.lastCameraPreset !== null) {
            this.lastCameraPreset = null;
            this.broadcastStatus('cameraPreset', this.lastCameraPreset);
        }

        this.suspend(suspendMs);

        if (this.newApi) {
            this.sendRequest('cameraMove', 'POST', path + index, {
                action: 'moveStart',
                direction: direction,
                message: 'BothCamera',
                withImage: 'No',
            });
        } else {
            this.sendRequest('cameraMove', 'POST', path + index, {
                action: 'moveStart',
                direction: direction,
            });
        }
    } else {
        if (this.newApi) {
            this.sendRequest('cameraMove', 'POST', path + index, {
                action: 'moveStop',
                direction: '',
                message: '',
                withImage: '',
            });
            this.sendRequest('cameraMove', 'POST', path + index, {
                action: 'moveStop',
                direction: '',
                message: '',
                withImage: '',
            });
        } else {
            // value.action === 'stop'
            this.sendRequest('cameraMove', 'POST', path + index, { action: 'moveStop' });
            this.sendRequest('cameraMove', 'POST', path + index, { action: 'moveStop' });
        }
    }
};
GroupSeries.prototype.commands.cameraMove.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.cameraPreset = {};
GroupSeries.prototype.commands.cameraPreset.get = function () {
    this.broadcastStatus('cameraPreset', this.lastCameraPreset);
};
GroupSeries.prototype.commands.cameraPreset.set = function (value) {
    if (
        !this.config.hasOwnProperty('cameraPresets') ||
        !this.config.cameraPresets.hasOwnProperty(value)
    ) {
        log.error(this + ': cameraPreset.set: invalid presetId ' + value);
        return;
    }

    if (!this.power) {
        this.set('power', true);
        return;
    }

    var path = this.newApi ? '/rest/current/cameras/near/presets/' : '/rest/cameras/near/presets/';
    this.lastCameraPreset = value;
    this.broadcastStatus('cameraPreset', this.lastCameraPreset);
    this.suspend(suspendMs);
    this.removeRequest('cameraPreset');
    this.sendRequest('cameraPreset', 'POST', path + this.config.cameraPresets[value].presetIndex, {
        action: 'activate',
    });
};
GroupSeries.prototype.commands.cameraPreset.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.cameraSelected = {};
GroupSeries.prototype.commands.cameraSelected.get = function () {
    for (var cameraId in this.cameraStates) {
        if (!this.cameraStates.hasOwnProperty(cameraId)) continue;
        this.broadcastStatus('cameraSelected', {
            cameraId: cameraId,
            value: this.cameraStates[cameraId].selected,
        });
    }
};
GroupSeries.prototype.commands.cameraSelected.set = function (cameraId) {
    if (!this.cameraCaps.hasOwnProperty(cameraId)) {
        log.error(this + ': cameraSelected.set: invalid cameraId ' + cameraId);
        return;
    }

    if (!this.power) {
        this.set('power', true);
        return;
    }

    if (!this.cameraStates.hasOwnProperty(cameraId)) {
        this.cameraStates[cameraId] = {
            selected: true,
            tracking: false,
        };
    } else {
        this.cameraStates[cameraId].selected = true;
    }

    this.broadcastStatus('cameraSelected', {
        cameraId: cameraId,
        value: true,
    });

    var path = this.newApi ? '/rest/cameras/near/' : '/rest/cameras/near/';

    this.suspend(suspendMs);
    this.removeRequest('cameraSelected');
    this.sendRequest('cameraSelected', 'POST', path + this.cameraInfo[cameraId].index, {
        action: 'peopleSelect',
    });
};
GroupSeries.prototype.commands.cameraSelected.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.cameraTracking = {};
GroupSeries.prototype.commands.cameraTracking.get = function () {
    for (var cameraId in this.cameraStates) {
        if (!this.cameraStates.hasOwnProperty(cameraId)) continue;
        this.broadcastStatus('cameraTracking', {
            cameraId: cameraId,
            value: this.cameraStates[cameraId].tracking,
        });
    }
};
GroupSeries.prototype.commands.cameraTracking.set = function (value) {
    if (!this.cameraInfo.hasOwnProperty(value.cameraId)) {
        log.error(this + ': cameraTracking.set: invalid cameraId ' + value.cameraId);
        return;
    }

    this.cameraStates[value.cameraId].tracking = value.value;
    this.broadcastStatus('cameraTracking', {
        cameraId: value.cameraId,
        value: value.value,
    });

    var varName = 'sourceman.camera' + this.cameraInfo[value.cameraId].index + '.trackingmode';
    var varValue = value.value
        ? this.previousCameraTrackingValue.hasOwnProperty(value.cameraId)
            ? this.previousCameraTrackingValue[value.cameraId]
            : 'framespeaker'
        : 'off';

    this.suspend(suspendMs);
    this.removeRequest('cameraTracking');
    var path = this.newApi ? '/rest/current/config' : '/rest/config';
    this.sendRequest('cameraTracking', 'POST', path, {
        vars: [
            {
                name: varName,
                value: varValue,
            },
        ],
    });
};
GroupSeries.prototype.commands.cameraTracking.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.contentState = {};
GroupSeries.prototype.commands.contentState.get = function () {
    for (var id in this.config.videoInputs) {
        if (!this.config.videoInputs.hasOwnProperty(id)) continue;
        if (id === this.noContentId) continue;
        this.broadcastStatus('contentState', {
            id: id,
            state: id === this.contentSource ? 'sending' : null,
        });
    }
};

GroupSeries.prototype.commands.contentSource = {};
GroupSeries.prototype.commands.contentSource.get = function () {
    return this.broadcastStatus('contentSource', this.contentSource);
};
GroupSeries.prototype.commands.contentSource.set = function (srcId) {
    if (this.getNode(this, ['config', 'videoInputs', srcId]) === null) {
        log.error(
            this +
                ': contentSource: set: srcId ' +
                srcId +
                ' not present in videoInputs for config = ' +
                JSON.stringify(this.config.videoInputs, null, 4)
        );
        return;
    }

    var changed = this.contentSource !== srcId;

    // Stop content on the current source.

    if (this.contentSource !== null && this.contentSource !== this.noContentId && changed) {
        this.broadcastStatus('contentState', {
            id: this.contentSource,
            state: null,
        });
        this.suspend(suspendMs);

        if (this.newApi) {
            // For testing with g7500, don't send end collaboration
            this.sendRequest(
                'contentSource',
                'POST',
                '/rest/cameras/near/' + this.config.videoInputs[this.contentSource].code,
                { action: 'contentStop' }
            );

            //            this.sendRequest('contentSource', 'POST', '/rest/current/collaboration', {
            //               action: 'End',
            //          });
        } else {
            this.sendRequest(
                'contentSource',
                'POST',
                '/rest/cameras/near/' + this.config.videoInputs[this.contentSource].code,
                { action: 'contentStop' }
            );
        }
        this.contentActive = false;
    }

    // Start content on the new source.

    this.contentSource = srcId;
    this.broadcastStatus('contentSource', this.contentSource);

    if (this.contentSource !== this.noContentId) {
        if (changed) {
            this.broadcastStatus('contentState', {
                id: this.contentSource,
                state: 'sending',
            });
        }

        this.suspend(suspendMs);
        this.sendRequest(
            'contentSource',
            'POST',
            '/rest/cameras/near/' + this.config.videoInputs[this.contentSource].code,
            { action: 'contentStart' }
        );
        // Regardless of response, we indicate content Active
        this.contentActive = true;
    }
};
GroupSeries.prototype.commands.contentSource.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.dial = {};
GroupSeries.prototype.commands.dial.set = function (value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error(this + ': dial: invalid callId ' + value.callId);
        return;
    }

    this.dialCall(ci, value.dialString);

    this.dialDigits += value.dialString;

    if (this.dialTimeout === null) this.onDialTimeout();
};
GroupSeries.prototype.commands.dial.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.firmwareVersion = {};
GroupSeries.prototype.commands.firmwareVersion.get = function () {
    this.broadcastStatus('firmwareVersion', this.firmwareVersion);
};

GroupSeries.prototype.commands.hangup = {};
GroupSeries.prototype.commands.hangup.set = function (callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error(this + ': hangup: invalid callId ' + callId);
        return;
    }

    if (!ci.hasOwnProperty('conferenceId') || !ci.hasOwnProperty('connectionId')) {
        log.debug(this + ": not hanging up call because it doesn't have connection info yet");
        return;
    }

    if (ci.conferenceId === 'PENDING') {
        log.debug(this + ': not hanging up call because it is still PENDING');
        return;
    }

    if (ci.callStatus.start > Date.now() - 2 * 1000) {
        log.debug(this + ': not hanging up call because it is too soon after connecting');
        return;
    }

    var path = this.newApi ? '/rest/current/conferences/' : '/rest/conferences/';

    this.endCall(ci);
    this.suspend(suspendMs);
    this.sendRequest('hangup', 'POST', path + ci.conferenceId + '/connections/' + ci.connectionId, {
        action: 'hangup',
    });
    this.queryCallsDeferred();
};
GroupSeries.prototype.commands.hangup.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.mute = {};
GroupSeries.prototype.commands.mute.get = function () {
    this.broadcastStatus('mute', this.mute);
};
GroupSeries.prototype.commands.mute.set = function (value) {
    this.mute = value;
    this.broadcastStatus('mute', this.mute);

    if (!this.power) this.set('power', true);

    var path = this.newApi ? '/rest/current/audio/muted' : '/rest/audio/muted';
    this.suspend(suspendMs);
    this.removeRequest('mute');
    this.sendRequest('mute', this.newApi ? 'POST' : 'PUT', path, this.mute);
};
GroupSeries.prototype.commands.mute.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.ping = {};
GroupSeries.prototype.commands.ping.activeStates = ['controlling', 'controlFailed'];
GroupSeries.prototype.commands.ping.get = function () {
    var path = this.newApi ? '/rest/current/system' : '/rest/system';
    this.sendRequest('ping', 'GET', path);
};
GroupSeries.prototype.commands.ping.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.ping.process = function (resp) {
    if (resp.hasOwnProperty('state')) {
        this.updatePower(resp.state !== 'ASLEEP');
    }
};

GroupSeries.prototype.commands.power = {};
GroupSeries.prototype.commands.power.activeStates = ['connecting', 'initializing', 'controlling'];
GroupSeries.prototype.commands.power.get = function () {
    this.broadcastStatus('power', this.power);
};
GroupSeries.prototype.commands.power.set = function (value) {
    var path = this.newApi ? '/rest/current/system' : '/rest/system';

    this.updatePower(value);
    if (this.fsm.current !== 'initializing') this.suspend(suspendMs);
    this.removeRequest('power');
    this.sendRequest('power', 'POST', path, {
        action: this.power ? 'wake' : 'sleep',
    });
};
GroupSeries.prototype.commands.power.match = function (resp) {
    return resp;
};
GroupSeries.prototype.commands.power.process = function (resp) {
    if (this.fsm.current === 'initializing') {
        this.doNextInitializeStep();
    }
};

GroupSeries.prototype.commands.reject = {};
GroupSeries.prototype.commands.reject.set = function (callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error(this + ': reject: invalid callId ' + callId);
        return;
    }

    if (!ci.hasOwnProperty('conferenceId') || !ci.hasOwnProperty('connectionId')) {
        log.debug(this + ": not rejecting call because it doesn't have connection info yet");
        return;
    }

    //if (ci.conferenceId === 'PENDING') {
    //    log.debug(this + ': not rejecting call because it is still PENDING');
    //    return;
    //}

    var path = this.newApi ? '/rest/current/conferences/' : '/rest/conferences';

    this.endCall(ci);
    this.suspend(suspendMs);
    this.sendRequest('hangup', 'POST', path + ci.conferenceId + '/connections/' + ci.connectionId, {
        action: 'hangup',
    });
    this.queryCallsDeferred();
};
GroupSeries.prototype.commands.reject.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.stopContent = {};
GroupSeries.prototype.commands.stopContent.set = function (srcId) {
    if (srcId === this.noContentId) return;
    this.set('contentSource', this.noContentId);
};

GroupSeries.prototype.commands.volume = {};
GroupSeries.prototype.commands.volume.get = function () {
    this.broadcastStatus('volume', this.volume);
};
GroupSeries.prototype.commands.volume.set = function (value) {
    this.volume = value;
    this.broadcastStatus('volume', this.volume);

    var slope = (this.volumeLimits.max - this.volumeLimits.min) / (volumeMax - volumeMin);
    var offset = this.volumeLimits.min - slope * volumeMin;
    var volume = clip(
        Math.round(slope * value + offset),
        this.volumeLimits.min,
        this.volumeLimits.max
    );
    var path = this.newApi ? '/rest/current/audio/volume' : '/rest/audio/volume';

    if (!this.power) this.set('power', true);
    this.removeRequest('volume');
    this.suspend(suspendMs);
    this.sendRequest('volume', this.newApi ? 'POST' : 'PUT', path, volume);
};
GroupSeries.prototype.commands.volume.match = function (resp) {
    return resp;
};

GroupSeries.prototype.commands.volumeMute = {};
GroupSeries.prototype.commands.volumeMute.get = function () {
    this.broadcastStatus('volumeMute', this.volumeMute);
};

GroupSeries.prototype.callStateChangedHandler = function (ci, prevCallState) {
    var callState = this.getCallState(ci);

    // Clear callExtendedState when a new call starts.

    if (callState === 'ringing' || (callState === 'connnected' && prevCallState !== 'ringing')) {
        ci.callStatus.callExtendedState = null;
    }

    // Clean up the call interfaces and recompute caps.

    this.cleanUpCallInterfaces();
    if (this.fsm.current === 'controlling') this.computeCaps();
};

GroupSeries.prototype.cleanUpCallInterfaces = function () {
    var availableIds = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
    var cis = {};
    var ci;
    var i;

    // Remove any disconnected call interfaces.

    for (i = 0; i < this.callInterfaces.length; i++) {
        ci = this.callInterfaces[i];

        if (this.getCallState(ci) === 'disconnected') {
            this.callInterfaces.splice(i--, 1);
        }
    }

    // Add call interfaces as necessary.

    for (i = 0; i < this.callInterfaces.length; i++) {
        ci = this.callInterfaces[i];

        if (
            !cis.hasOwnProperty(ci.callType) &&
            (ci.callType === 'video' || this.getCallState(ci) === 'disconnected')
        ) {
            cis[ci.callType] = ci;
        }

        var idx = availableIds.indexOf(parseInt(ci.configId.substring(1), 10));
        if (idx >= 0) availableIds.splice(idx, 1);
    }

    ['video', 'voip'].forEach(function (callType) {
        if (callType === 'voip' && (!this.sipEnabled || !this.config.audioDialingEnabled)) return;

        if (!cis.hasOwnProperty(callType)) {
            i = availableIds.shift();

            if (typeof i !== 'undefined') {
                this.callInterfaces.push({
                    configId: 'a' + i,
                    name: 'interface' + i,
                    callType: callType,
                    connectionId: 'dummy' + i,
                });
            } else {
                log.error(this + ": out of available ids; can't add " + callType + 'interface');
            }
        }
    }, this);
};

GroupSeries.prototype.convertMediaTypeToCallType = function (mediaType) {
    if (mediaType === 'AUDIOONLY') return 'voip';
    return 'video';
};

GroupSeries.prototype.deleteTerminal = function (terminal) {
    if (this.terminals.hasOwnProperty(terminal.parentConfId)) {
        if (this.terminals[terminal.parentConfId].hasOwnProperty(terminal.parentConnectionId)) {
            delete this.terminals[terminal.parentConfId][terminal.parentConnectionId][terminal.id];

            if (
                Object.keys(this.terminals[terminal.parentConfId][terminal.parentConnectionId])
                    .length === 0
            ) {
                delete this.terminals[terminal.parentConfId][terminal.parentConnectionId];
            }
        }

        if (Object.keys(this.terminals[terminal.parentConfId]).length === 0) {
            delete this.terminals[terminal.parentConfId];
        }
    }
};

GroupSeries.prototype.getCallInterfaceByConnection = function (connectionId, callType, incoming) {
    var ci;
    var i;

    for (i = 0; i < this.callInterfaces.length; i++) {
        ci = this.callInterfaces[i];

        if (ci.connectionId === connectionId) {
            return ci;
        }
    }

    if (callType === null) return null;

    ci = this.getAvailableCallInterface(callType);

    if (ci === null) {
        var configId = 0;

        for (i = 0; i < this.callInterfaces.length; i++) {
            var n = parseInt(this.callInterfaces[i].configId.substring(1), 10);
            if (n > configId) configId = n;
        }

        configId++;

        this.callInterfaces.push({
            configId: 'a' + configId,
            name: 'interface' + configId,
            callType: callType,
            connectionId: 'dummy' + configId,
        });

        ci = this.callInterfaces[this.callInterfaces.length - 1];
        log.info(this + ': added ' + callType + ' call interface');

        // Hack for SNTY-1673.

        if (incoming) this.setIncomingCall(ci);

        if (this.fsm.current === 'controlling') this.computeCaps();
    }

    if (ci !== null) {
        ci.connectionId = connectionId;
    }

    return ci;
};

GroupSeries.prototype.getConfigVar = function (data, name) {
    for (var i = 0; i < data.vars.length; i++) {
        if (this.newApi) {
            if (data.vars[i].name === name && data.vars[i].result === 'NOERROR') {
                return data.vars[i];
            }
        } else {
            if (
                data.vars[i].hasOwnProperty('configVar') &&
                data.vars[i].configVar.hasOwnProperty('nodeAndVarName') &&
                data.vars[i].configVar.nodeAndVarName === name
            ) {
                return data.vars[i];
            }
        }
    }

    return null;
};

GroupSeries.prototype.handleConferenceEvent = function (entry) {
    var i;

    switch (entry.name) {
        case 'onConferenceChanged':
            for (i = 0; i < entry.object.connections.length; i++) {
                this.updateConnection(entry.object.connections[i]);
            }

            for (i = 0; i < entry.object.terminals.length; i++) {
                this.updateTerminal(entry.object.terminals[i]);
            }
            break;
        case 'onConnectionChanged':
        case 'onConnectionStateChanged':
            this.updateConnection(entry.object);
            break;
        case 'onTermAdded':
        case 'onTermChanged':
            this.updateTerminal(entry.object);
            break;
        case 'onConferenceClosed':
        case 'onTermDeleted':
            this.deleteTerminal(entry.object);
            break;
        default:
            break;
    }
};

GroupSeries.prototype.initializeCallInterfaces = function (conferences) {
    var ignoreStates = ['ABORTED', 'ALLOCATED', 'DISCONNECTED', 'FAILED', 'INACTIVE'];

    for (var i = 0; i < conferences.length; i++) {
        var conference = conferences[i];

        for (var j = 0; j < conference.connections.length; j++) {
            var connection = conference.connections[j];

            if (ignoreStates.includes(connection.state)) {
                continue; // Don't make callInterface entries for these states.
            }

            var ci = this.getCallInterfaceByConnection(
                connection.id,
                this.convertMediaTypeToCallType(connection.mediaType)
            );
            var contactName = null;

            for (var k = 0; k < conference.terminals.length; k++) {
                var terminal = conference.terminals[k];

                if (terminal.hasOwnProperty('name')) {
                    contactName = terminal.name;
                    break;
                } else if (terminal.hasOwnProperty('callerID')) {
                    contactName = terminal.callerID;
                    break;
                }
            }

            ci.conferenceId = connection.parentConfId;
            ci.callStatus = {
                callId: this.generateId(),
                callType: 'video',
                direction: connection.incoming ? 'incoming' : 'outgoing',
            };

            if (connection.hasOwnProperty('address')) {
                ci.callStatus.dialString = connection.address;
            }

            if (contactName !== null) ci.callStatus.contactName = contactName;

            switch (connection.state) {
                case 'CONNECTED':
                    ci.callStatus.callState = 'connected';
                    ci.callStatus.start = Date.now() - connection.duration * 1000;
                    break;

                case 'DIALING':
                    ci.callStatus.callState = 'ringing';
                    ci.callStatus.start = Date.now(); // duration not valid
                    break;

                case 'CONNECTING':
                case 'RINGING':
                case 'INCOMING':
                    ci.callStatus.callState = 'ringing';
                    ci.callStatus.start = Date.now() - connection.duration * 1000;
                    break;

                case 'DISCONNECTNG': // haven't actually seen this state yet
                    ci.callStatus.callState = 'disconnecting';
                    ci.callStatus.start = Date.now() - connection.duration * 1000;
                    ci.callStatus.end = Date.now();
                    break;

                case 'ABORTED':
                case 'ALLOCATED':
                case 'DISCONNECTED':
                case 'FAILED':
                case 'INACTIVE':
                    // We shouldn't get here because of the if...continue at
                    // the beginning of the function.
                    log.error(
                        this +
                            ': $calls: short circuit failed for connection state: ' +
                            connection.state
                    );
                    break;

                default:
                    log.error(this + ': $calls: unhandled connection state: ' + connection.state);
                    break;
            }
        }
    }
};

GroupSeries.prototype.initializeSteps = [
    {
        state: 'waking',
        action: function () {
            // Power on the Group Series when we connect so the camera caps will
            // be reported correctly.
            this.set('power', true);
            return true;
        },
    },
    {
        state: 'gettingVersion',
        action: '$version',
    },
    {
        state: 'gettingMute',
        action: '$initialMute',
    },
    {
        state: 'gettingVolume',
        action: '$initialVolume',
    },
    {
        state: 'gettingSipEnabled',
        action: '$sipEnabled',
    },
    {
        state: 'gettingCalls',
        action: '$calls',
    },
    {
        state: 'gettingCameras',
        action: '$cameras',
    },
    {
        state: 'gettingCameraTracking',
        action: '$cameraTracking',
    },
    {
        state: 'registeringNotifications',
        action: '$registerNotifications',
    },
    {
        state: 'finishInitialization',
        action: function () {
            this.finishInitialization();
            return true;
        },
    },
];

GroupSeries.prototype.onComputeCaps = function () {
    this.cleanUpCallInterfaces();

    log.debug(
        this + ': onComputeCaps: callInterfaces = ' + JSON.stringify(this.callInterfaces, null, 4)
    );

    var caps = this.getCapsTemplate();
    caps.hasCalendar = this.config.hasOwnProperty('useCalendar') && this.config.useCalendar;
    caps.hasBusy = true;
    caps.hasFirmwareVersion = true;
    caps.hasRoomMute = true;
    caps.hasPowerControl = true;
    caps.hasRoomVolume = true;
    caps.hasRoomVolumeMute = true;
    caps.hasZoomConnector = this.config.zoomConnectorEnabled === true;
    caps.videoInputsAreContent = true;
    caps.hasVideoSwitcher = true;
    this.computeCapsMaxCalls(caps);
    this.computeVideoInputsCaps(caps);
    if (!caps.hasVideoInputs) caps.hasVideoSwitcher = false;
    caps.cameras = JSON.parse(JSON.stringify(this.cameraCaps));
    this.computeCameraPresetsCaps(caps);

    return caps;
};

GroupSeries.prototype.onConnect = function () {
    this.newApi = true;

    this.resetCallInterfaces();

    // Send a request to log out of any previously active session. This will
    // then trigger a request to log in, which will finish the connecting
    // process. We try the new api first, then fallback to the old one if the
    // new api fails.

    this.query('$logOutCurrent');
};

GroupSeries.prototype.onControl = function () {
    this.finishControlling();
};

// If you send DTMF digits too quickly, the Group Series will not play some of
// them. Slowing down to a rate of ~350 ms seems to resolve the problem.

GroupSeries.prototype.onDialTimeout = function () {
    this.dialTimeout = null;

    if (this.dialDigits.length <= 0) return;

    var path = this.newApi ? '/rest/current/audio' : '/rest/audio';

    this.suspend(suspendMs);
    this.sendRequest('dial', 'POST', path, {
        action: 'playDTMF',
        dtmfChar: this.dialDigits[0],
        //where: 'both',
    });

    this.dialDigits = this.dialDigits.slice(1);

    this.dialTimeout = window.setTimeout(this.onDialTimeout.bind(this), 400);
};

GroupSeries.prototype.onInitialize = function () {
    this.busy = false;
    this.volume = 0;
    this.mute = true;
    this.volumeMute = true;
    this.model = null;
    this.firmwareVersion = null;
    this.lastCameraPreset = null;
    this.sipEnabled = false;
    this.terminals = {};
    this.cameraCaps = {};
    this.cameraInfo = {};
    this.cameraStates = {};
    this.previousCameraTrackingValue = {};
    this.pollCycle = 0;
    this.longPollCycle = 0;

    this.callInterfaces = []; // This will get populated in one of the initialize steps.

    this.startInitializer();
};

GroupSeries.prototype.onPoll = function () {
    this.pollCycle++;
    this.longPollCycle++;

    if (this.pollCycle === 1) {
        this.query('$cameraTracking');
        this.query('$contentActive');
    } else if (this.pollCycle === 2) {
        this.query('$cameras');
    } else {
        if (!this.hasRequest('$calls')) this.query('$calls');

        // We don't get notifications for sipEnabled in the new API, so we have
        // to poll it.

        if (this.newApi) this.query('$sipEnabled');
        this.pollCycle = 0;
    }

    // We have seen some installations where the Group Series does not send notifications when
    // new meetings are added to the calendar. We're not sure if that's a change in newer
    // versions of their software or if it is a provider difference (e.g, on-prem Exchange vs
    // Office 365 in the cloud). We still listen for notifications, but we also poll here every
    // 30 seconds in order to handle both cases. Note we poll at the beginning of the long poll
    // cycle so we don't have to query the meetings during initialization any more. Also we poll
    // even if the calendar isn't enabled in the device config -- if a user enables the calendar,
    // then they will get the latest meetings immediately rather than having to wait 30 seconds.

    if (this.longPollCycle === 1) {
        this.query('$meetings');
    }

    if (this.longPollCycle >= 10) this.longPollCycle = 0;
};

GroupSeries.prototype.onUpdateConfig = function () {
    // Don't call CallsDevice.onUpdateConfig(this); we are manually defining our
    // own callInterfaces.
};

// conferences = [{
//     id: string, typically "1", "2", etc., but could be "PENDING"
//     connections: [{
//         address: string, typically IP address, e.g., "1.2.3.4"
//         id: string,
//         incoming: boolean,
//         parentConfId: string,
//         duration: number, seconds or milliseconds?
//         state: string, seen so far: "ALLOCATED", "DIALING", "CONNECTING", "CONNECTED", "DISCONNECTED", "INACTIVE"
//     }],
//     terminals: [{
//         address: string, typically IP address, e.g., "1.2.3.4",
//         callerId: string,
//         name: string,
//         parentConnectionId: string,
//     }]
// }]

GroupSeries.prototype.parseConferences = function (resp) {
    var conferences = [];

    for (var i = 0; i < resp.length; i++) {
        var conference = resp[i];
        var j;

        var conferenceInfo = {
            id: conference.id,
            connections: [],
            terminals: [],
        };

        for (j = 0; j < conference.connections.length; j++) {
            var connection = conference.connections[j];

            conferenceInfo.connections.push({
                address: connection.address,
                duration: connection.duration,
                id: connection.id,
                incoming: connection.incoming,
                parentConfId: connection.parentConfId,
                state: connection.state,
                mediaType: connection.mediaType,
            });
        }

        for (j = 0; j < conference.terminals.length; j++) {
            var terminal = conference.terminals[i];

            conferenceInfo.terminals.push({
                address: terminal.address,
                callerId: terminal.callerId,
                name: terminal.name,
                parentConnectionId: terminal.parentConnectionId,
            });
        }

        conferences.push(conferenceInfo);
    }

    return conferences;
};

GroupSeries.prototype.parseMeetingBody = function (body) {
    var description = body;

    description = description.replace(/\\r\\n/g, '\r\n');
    description = description.replace(/(\S+)(\s*)<(mailto:[^>]+)>/g, '<a href="$3">$1</a>$2');
    description = description.replace(
        /(\S+)(\s*)<((?:http:https):\/\/[^>]>)/g,
        '<a href="$3">$1</a>$2'
    );
    description =
        '<html>' +
        '<head><meta http-equiv="Content-Type" content="text/html; charset=utf-8"></head>' +
        '<body>' +
        description +
        '</body>' +
        '</html>';

    return description;
};

GroupSeries.prototype.queryCallsDeferred = function () {
    this.defer(
        function () {
            if (!this.hasRequest('$calls')) this.query('$calls');
        }.bind(this),
        2000
    );
};

GroupSeries.prototype.updateCallInterfaces = function (conferences) {
    var connectionIds = [];
    var i, j;

    for (i = 0; i < conferences.length; i++) {
        var conference = conferences[i];

        for (j = 0; j < conference.connections.length; j++) {
            var connection = conference.connections[j];
            connectionIds.push(connection.id);
            this.updateConnection(connection);
        }

        for (j = 0; j < conference.terminals.length; j++) {
            this.updateTerminal(conference.terminals[j]);
        }
    }

    for (i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];

        if (this.getCallState(ci) === 'disconnected') continue;

        if (!connectionIds.includes(ci.connectionId)) {
            log.info(
                this +
                    ': found orphaned call interface, disconnecting: ci = ' +
                    JSON.stringify(ci, null, 4)
            );
            this.disconnectCall(ci);
        }
    }
};

GroupSeries.prototype.updateConnection = function (connection) {
    var callType = this.convertMediaTypeToCallType(connection.mediaType);
    var needsBroadcast = false;
    var ignoreStates = ['ALLOCATED'];
    var ci = null;
    var i;

    if (ignoreStates.includes(connection.state)) return;

    // See if there's a call interface with this connection id.

    for (i = 0; i < this.callInterfaces.length; i++) {
        if (
            this.callInterfaces[i].hasOwnProperty('connectionId') &&
            this.callInterfaces[i].connectionId === connection.id
        ) {
            ci = this.callInterfaces[i];
            break;
        }
    }

    if (ci === null) {
        // Don't add a new call interface if there's no match and the call
        // would be disconnected anyway.

        if (['DISCONNECTED', 'INACTIVE', 'ABORTED', 'FAILED'].includes(connection.state)) return;

        // If there was no match for the connection id, then this is either:
        //     an outgoing call for which we haven't yet assigned the connection id
        // or:
        //     an incoming call, or a call initiated by another controller

        if (!connection.incoming && typeof this.saveCallId !== 'undefined') {
            for (i = 0; i < this.callInterfaces.length; i++) {
                if (
                    this.callInterfaces[i].hasOwnProperty('callStatus') &&
                    this.callInterfaces[i].callStatus.callId === this.saveCallId
                ) {
                    ci = this.callInterfaces[i];
                    ci.connectionId = connection.id;
                    delete this.saveCallId;
                }
            }

            if (ci === null) {
                log.error(this + ': saveCallId set, but no appropriate call interface');
                return;
            }
        } else {
            ci = this.getCallInterfaceByConnection(connection.id, callType, true);
        }
    }

    // Under some circumstances, the call type can dynamically change.

    if (ci.callType !== callType) {
        log.debug(this + ': changing callType from ' + ci.callType + ' to ' + callType);
        ci.callType = callType;
        needsBroadcast = true;
    }

    if (ci.hasOwnProperty('callStatus') && ci.callStatus.callType !== callType) {
        log.debug(
            this +
                ': changing callStatus.callType from ' +
                ci.callStatus.callType +
                ' to ' +
                callType
        );
        ci.callStatus.callType = callType;
        needsBroadcast = true;
    }

    var contactName; // undefined unless set below
    var dialString; // undefined unless set below

    // See if there's a terminal object with contactName information.

    if (
        this.terminals.hasOwnProperty(connection.parentConfId) &&
        this.terminals[connection.parentConfId].hasOwnProperty(connection.id)
    ) {
        var terminals = this.terminals[connection.parentConfId][connection.id];
        var terminalKeys = Object.keys(terminals).sort();

        if (terminalKeys.length > 0) {
            var terminal = terminals[Object.keys(terminals).sort()[0]];
            contactName = terminal.name ? terminal.name : terminal.callerId;
        }
    }

    // If the call was placed by another controller, set the dial string from
    // the connection's address.

    if (!ci.hasOwnProperty('callStatus') || ci.callStatus.callState === 'disconnected') {
        dialString = connection.address;
    }

    var callExtendedState = null;

    switch (connection.state) {
        case 'DIALING':
            if (!connection.incoming) {
                if (
                    this.getNode(ci, 'callStatus.callState') === 'connected' &&
                    ci.callStatus.start > Date.now() - stateChangeWaitMs
                ) {
                    log.debug(
                        this +
                            ': ignoring ' +
                            connection.state +
                            ' state for callId ' +
                            ci.callStatus.callId +
                            ' because it is connected'
                    );
                } else {
                    ci.conferenceId = connection.parentConfId;
                    this.setOutgoingCall(ci, dialString, contactName);
                    needsBroadcast = false;
                }
            } else {
                log.error(
                    this +
                        ': incoming = true on connection in DIALING state: ' +
                        JSON.stringify(connection, null, 4)
                );
            }
            break;
        case 'RINGING':
        case 'INCOMING':
            if (connection.incoming) {
                ci.conferenceId = connection.parentConfId;

                if (
                    ci.hasOwnProperty('callStatus') &&
                    ci.callStatus.callState === 'connected' &&
                    ci.callStatus.start > Date.now() - stateChangeWaitMs
                ) {
                    log.debug(
                        this +
                            ': ignoring ' +
                            connection.state +
                            ' state for callId ' +
                            ci.callStatus.callId +
                            ' because it is connected'
                    );
                } else if (
                    ci.hasOwnProperty('callStatus') &&
                    ci.callStatus.callState === 'disconnecting' &&
                    ci.callStatus.end > Date.now() - stateChangeWaitMs
                ) {
                    log.debug(
                        this +
                            ': ignoring ' +
                            connection.state +
                            ' state for callId ' +
                            ci.callStatus.callId +
                            ' because it is disconnecting'
                    );
                } else {
                    this.setIncomingCall(ci, connection.address, contactName);
                    needsBroadcast = false;
                    callExtendedState = connection.answerable ? null : 'answerWillEnd';
                }
            } else {
                log.error(
                    this +
                        ': incoming = false on connection in RINGING state: ' +
                        JSON.stringify(connection, null, 4)
                );
            }

            break;
        case 'CONNECTING':
            break;
        case 'CONNECTED':
            if (
                ci.hasOwnProperty('callStatus') &&
                ci.callStatus.callState === 'disconnecting' &&
                ci.callStatus.end > Date.now() - stateChangeWaitMs
            ) {
                log.debug(
                    this +
                        ': ignoring CONNECTED state for callId ' +
                        ci.callStatus.callId +
                        ' because it is disconnecting'
                );
            } else {
                ci.conferenceId = connection.parentConfId;
                this.connectCall(ci, null, dialString);
                needsBroadcast = false;
            }
            break;
        case 'DISCONNECTING': // haven't actually seen this state
            ci.conferenceId = connection.parentConfId;
            this.endCall(ci);
            break;
        case 'ABORTED':
        case 'FAILED':
        case 'INACTIVE':
        case 'DISCONNECTED':
            ci.conferenceId = connection.parentConfId;
            this.disconnectCall(ci);
            break;
        case 'ALLOCATED':
            log.error(
                this +
                    ': updateConnection: short circuit failed for connection state: ' +
                    connection.state
            );
            break;
        default:
            log.error(this + ': updateConnection: unhandled connection state: ' + connection.state);
            break;
    }

    if (this.getNode(ci, ['callStatus', 'callExtendedState']) !== callExtendedState) {
        this.setNode(ci, ['callStatus', 'callExtendedState'], callExtendedState);
        needsBroadcast = true;
    }

    if (needsBroadcast) {
        this.broadcastStatus('callStatus', JSON.parse(JSON.stringify(ci.callStatus)));
    }
};

GroupSeries.prototype.updateMute = function (value) {
    if (value !== this.mute) {
        this.mute = value;

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('mute', this.mute);
        }
    }
};

GroupSeries.prototype.updateSipEnabled = function (value) {
    if (value !== this.sipEnabled) {
        this.sipEnabled = value;

        this.cleanUpCallInterfaces();
        if (this.fsm.current === 'controlling') this.computeCaps();
    }
};

GroupSeries.prototype.updatePower = function (value) {
    if (this.power !== value) {
        this.power = value;

        if (this.power) {
            this.setBusy(true, 0);
            this.setBusy(false, 20000);
        }

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('power', this.power);
        }
    }
};

GroupSeries.prototype.updateTerminal = function (terminal) {
    if (!this.terminals.hasOwnProperty(terminal.parentConfId)) {
        this.terminals[terminal.parentConfId] = {};
    }

    if (!this.terminals[terminal.parentConfId].hasOwnProperty(terminal.parentConnectionId)) {
        this.terminals[terminal.parentConfId][terminal.parentConnectionId] = {};
    }

    this.terminals[terminal.parentConfId][terminal.parentConnectionId][terminal.id] = {
        callerId: terminal.callerId,
        name: terminal.name,
    };

    var ci = this.getCallInterfaceByConnection(terminal.parentConnectionId, null);

    if (
        ci === null ||
        !ci.hasOwnProperty('callStatus') ||
        ci.callStatus.callState === 'disconnected'
    )
        return;

    var contactName = terminal.name ? terminal.name : terminal.callerID;
    var undef;

    this.updateCallerId(ci, undef, contactName, undef, true);
};

GroupSeries.prototype.updateVolume = function (value) {
    if (typeof value === 'string') {
        value = parseInt(value, 10);
    }

    var slope = (volumeMax - volumeMin) / (this.volumeLimits.max - this.volumeLimits.min);
    var offset = volumeMin - slope * this.volumeLimits.min;
    var newVolume = clip(Math.floor(slope * value + offset), volumeMin, volumeMax);

    if (newVolume !== this.volume) {
        this.volume = newVolume;

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('volume', this.volume);
        }
    }

    var newVolumeMute = newVolume === 0;

    if (newVolumeMute !== this.volumeMute) {
        this.volumeMute = newVolumeMute;

        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('volumeMute', this.volumeMute);
        }
    }
};

module.exports = GroupSeries;
